<template>
  <div class="mb-4">
    <div
      class="bg-custom-light-green rounded-top d-lg-flex flex-row px-3 py-2 align-items-center bd-highlight"
    >
      <div class="d-flex flex-row w-80 bd-highlight">
        <div class="color-secondary-6 font-14 mr-1">
          Nombre de Custodio:
        </div>
        <div class="font-weight-bold font-16">
          {{ userName | empty }}
        </div>
      </div>
      <div v-if="isValidTransaction">
        <custom-status-transaction
          :source="transactionMaterial"
          :destination="transactionOrder"
        />
      </div>
    </div>

    <custom-callout
      has-toggle
      :opened="isPanelOpen"
      :controlled="true"
      :border-color="item.statusColor"
      @click:open="onToggle()"
      class="border-radius-top-auto"
    >
      <div class="row m-0 d-flex align-items-center">
        <div class="col-md col-12 p-md-0">
          <div class="py-3">
            <custom-balloon
              dot
              class="mt-2"
              :background-color="item.statusColor"
              :text="item.status?.name"
            />
          </div>
        </div>
        <div class="col-md-9 col-12 mb-3 mb-lg-0 pl-md-0">
          <div class="row font-weight-normal">
            <div class="col-md col-12">
              <div class="color-secondary-6 font-14 ">
                Número de Solicitud:
              </div>
              <div class="color-black font-16">
                {{ item.requestCode | empty }}
              </div>
            </div>
            <div class="col-md col-12">
              <div class="color-secondary-6 font-14">
                Título del Material/Equipo:
              </div>
              <div class="color-black font-16">
                {{ item.material?.name | empty }}
              </div>
            </div>
            <div class="col-md col-12">
              <div class="color-secondary-6 font-14">
                Categoría:
              </div>
              <div class="color-black font-16">
                {{ item.category?.name | empty }}
              </div>
            </div>
            <div class="col-md-3 col-12">
              <div class="color-secondary-6 font-14">
                Tipo de Salón:
              </div>
              <div class="color-black font-16">
                {{ item.saloonType?.name | empty }}
              </div>
            </div>
            <div class="col-md-2 col-12">
              <drop-down
                :items="actionItems"
                always-show-dropdown
                class="float-md-right mt-2 mt-md-1"
              />
            </div>
          </div>
        </div>
      </div>
      <template #edition>
        <div v-if="showTrackedInfo" class="bg-light-gray p-4">
          <div class="px-3 rounded-top bg-white">
            <custom-tab
              content-class="mt-3"
              nav-class="nav-vue-underline"
              no-nav-style
              @input="setCurrentTab"
              :defaultActiveTab="activeTab"
            >
              <custom-tab-item title="Información de Solicitud">
                <tracked-info :requestId="item.id" />
              </custom-tab-item>
              <custom-tab-item
                v-if="item?.hasPurchases"
                title="Información de Compra"
              >
                <purchase-info :requestId="item.id" />
              </custom-tab-item>
              <custom-tab-item title="Historial de Solicitud">
                <request-history :orderDetail="item" />
              </custom-tab-item>
              <custom-tab-item v-if="hasDocuments" title="Documentos">
                <documents-info :orderDetail="item" />
              </custom-tab-item>
            </custom-tab>
          </div>
        </div>
        <div v-if="showRejectForm">
          <custom-request-form
            form-title="Razón de Rechazo"
            custom-icon="fa fa-times-circle text-red"
            :form-info="item"
            class="bg-custom-light-tracked rounded-top"
            @close="onClosePanel"
            @save="rejectRequest"
            :action="actions.Reject"
          />
        </div>
        <div v-if="showSchoolDeliveryForm">
          <custom-request-form
            form-title="Recibido Escuela"
            form-date-title="Fecha de Entrega:"
            custom-icon="fas fa-calendar-edit text-orange"
            :form-info="item"
            class="bg-custom-light-tracked rounded-top"
            @close="onClosePanel"
            @save="changeStatusToPendingDeliveryTeacher"
            :show-input-date="true"
            :action="actions.DeliverToSchool"
          />
        </div>

        <div v-if="showTeacherDeliveryForm">
          <custom-request-form
            form-title="Recibido Maestro"
            form-date-title="Fecha:"
            custom-icon="fas fa-calendar-edit text-orange"
            :form-info="item"
            class="bg-custom-light-tracked rounded-top"
            @close="onClosePanel"
            @save="changeStatusToDelivered"
            :show-action-buttons="true"
            :show-input-date="true"
            :action="actions.ReceivedByMaster"
          />
        </div>

        <div v-if="showApprovalPurchaseForm">
          <custom-request-form
            form-title="Información de Aprobación"
            form-date-title="Fecha de Compra:"
            custom-icon="fas fa-check-circle text-orange"
            :form-info="item"
            class="bg-custom-light-tracked rounded-top"
            @close="onClosePanel"
            @save="approveOrderPurchase"
            :show-action-buttons="true"
            :show-input-date="true"
            :action="actions.ApprovePurchase"
            :isDateReadOnly="true"
          />
        </div>

        <div v-if="showDeliveryToCseeForm">
          <custom-request-form
            form-title="Recibido CSEE/ORE"
            custom-icon="fa fa-calendar-edit text-orange"
            :form-info="item"
            :min-date="deliveryToCseeMinDate"
            :max-date="new Date()"
            :show-input-date="true"
            class="bg-custom-light-tracked rounded-top"
            @close="onClosePanel"
            @save="deliveryCsee"
            :action="actions.DeliveryCsee"
          />
        </div>

        <div v-if="showAssignedMaterial" class="bg-grey py-4">
          <div class="bg-white m-3 p-2 rounded">
            <icon-view
              :has-border="false"
              icon="fas fa-file-user text-orange"
              title="Material/Equipo Seleccionado en Inventario"
              variant="custom-icon-view"
              icon-background="white"
            />
            <div class="m-4">
              <assigned-row
                can-see-action-from-another-view
                :item="material"
                from-result-request-row
                @open="handleAction"
                @close="handleAction"
              />
            </div>
          </div>
        </div>
        <div v-if="showConfirmationPurchaseForm">
          <confirm-purchase
            :order="item"
            @close="onClosePanel"
            @save="onConfirmPurchase"
          />
        </div>
      </template>
    </custom-callout>
  </div>
</template>

<script>
import IconView from 'icon-view';
import { CustomTab, CustomTabItem } from 'custom-tabs';
import CustomCallout from 'custom-callout';
import DropDown from 'dropdown';
import CustomBalloon from 'custom-balloon';
import TrackedInfo from '../../materials-and-equipments-requests/components/TrackedInfo.vue';
import PurchaseInfo from './PurchaseInfo.vue';
import RequestHistory from '../../materials-and-equipments-requests/components/RequestHistory.vue';
import DocumentsInfo from './DocumentsInfo.vue';
import CustomStatusTransaction from './CustomStatusTransaction.vue';
import CustomRequestForm from './CustomRequestForm';
import ConfirmPurchase from './ConfirmPurchase.vue';
import AssignedRow from '@/components/pei/materials-and-equipments/Management/components/ResultRow.vue';
import { studentBookStatus as studentMaterialStatus } from '@/utils/constants/book/studentBookStatus.js';
import inventorystudentMaterialStatusReasons from '@/utils/constants/inventory/inventoryStudentBookStatusReasons.js';
import inventoryOrderMaterialsAndEquipments from '@/services/api/inventoryOrderMaterialsAndEquipments.api';
import purchaseReasonsConst from '@/utils/constants/book/purchaseReasons';
import inventoryMaterialsAndEquipments from '@/services/api/materialsAndEquipments.api';
import ACTIONS from '@/utils/constants/inventoryOrderMaterialEquipmentsRowAction';
import transferStatuses from '@/utils/constants/inventory/peiAtServiceTransferStatus';
import moment from 'moment';
import alertMixin from '@/mixin/alert.mixin';

const TEMPLATE_TAB_INDEX = {
  TRACKEDREQUEST: 0,
  PURCHASEINFO: 1,
  ORDERHISTORY: 2,
  DOCUMENTS: 3,
};
const TEMPLATE_ACTION_INDEX = {
  INFO: 0,
  REJECTREQUEST: 1,
  DELIVERTOSCHOOL: 2,
  RECIVEDBYMASTER: 3,
  CHECKASSIGNEDBOOK: 4,
  APPROVEPURCHASE: 5,
  CONFIRMPURCHASE: 7,
  DELIVERYTOCSEE: 8,
};
export default {
  name: 'ResultRow',
  components: {
    CustomCallout,
    DropDown,
    CustomBalloon,
    TrackedInfo,
    CustomTab,
    CustomTabItem,
    CustomRequestForm,
    CustomStatusTransaction,
    AssignedRow,
    IconView,
    ConfirmPurchase,
    PurchaseInfo,
    RequestHistory,
    DocumentsInfo,
  },
  mixins: [alertMixin],
  props: {
    item: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    fromRequest: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    actionsToShow: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data() {
    return {
      selectedTemplateTabIndex: null,
      isOpen: false,
      activeTab: TEMPLATE_TAB_INDEX.TRACKEDREQUEST,
      currentAction: null,
      actions: ACTIONS,
      material: {},
    };
  },
  computed: {
    actionItems() {
      const actions = [
        {
          id: ACTIONS.ViewInventory,
          action: {
            name: 'Ver Inventario',
            icon: 'far fa-file-export text-success',
            class: 'border-bottom',
            visible: this.canViewInventory,
            click: () => {
              this.$router.push({
                name: 'inventoryRequestsMaterialsAndEquipments',
                params: { id: this.item.id },
              });
            },
          },
        },
        {
          id: ACTIONS.ContinueMaterialRequest,
          action: {
            name: 'Continuar Pedido',
            icon: 'fas fa-plus-circle text-success',
            class: 'border-bottom',
            visible:
              studentMaterialStatus.PendientePedido == this.item?.status?.id,
            click: () => {
              this.$router.push({
                name: 'inventoryOrderMaterialsAdd',
              });
            },
          },
        },
        {
          id: ACTIONS.Info,
          action: {
            name: 'Información de Solicitud',
            icon: 'fas fa-info-circle text-success',
            click: async () => {
              this.showRequestInfo();
            },
          },
        },
        {
          id: ACTIONS.History,
          action: {
            name: 'Historial de Solicitud',
            icon: 'fas fa-history text-success',
            click: async () => {
              this.showOrderHistory();
            },
          },
        },
        {
          id: ACTIONS.Documents,
          action: {
            name: 'Documentos',
            icon: 'fas fa-file text-success',
            visible: this.hasDocuments,
            click: async () => {
              this.showDocuments();
            },
          },
        },
        {
          id: ACTIONS.ViewAssignedMaterial,
          action: {
            name: 'Ver Material Asignado',
            icon: 'fas fa-file-search text-success',
            visible: this.canSeeAssignedMaterial,
            click: async () => {
              await this.getMaterialDetail();
              this.currentAction = TEMPLATE_ACTION_INDEX.CHECKASSIGNEDBOOK;
              this.$emit('open', this.item);
            },
          },
        },
        {
          id: ACTIONS.Reject,
          action: {
            name: 'Rechazar',
            icon: 'fas fa-times-square text-danger',
            visible: this.canSeeRejectForm,
            click: async () => {
              this.currentAction = TEMPLATE_ACTION_INDEX.REJECTREQUEST;
              this.$emit('open', this.item);
            },
          },
        },
        {
          id: ACTIONS.DeliverToSchool,
          action: {
            name: 'Entrega Escuela',
            icon: 'fas fa-calendar-edit text-success',
            visible: this.canSeeSchoolDeliveryForm,
            click: async () => {
              this.currentAction = TEMPLATE_ACTION_INDEX.DELIVERTOSCHOOL;
              this.$emit('open', this.item);
            },
          },
        },
        {
          id: ACTIONS.ReceivedByMaster,
          action: {
            name: 'Recibido Maestro',
            icon: 'fas fa-calendar-edit text-success',
            visible: this.canSeeMasterDeliveryForm,
            click: async () => {
              this.currentAction = TEMPLATE_ACTION_INDEX.RECIVEDBYMASTER;
              this.$emit('open', this.item);
            },
          },
        },
        {
          id: ACTIONS.ApprovePurchase,
          action: {
            name: 'Aprobar Compra',
            icon: 'fas fa-check-circle text-success',
            visible: this.isPurchaseApprovalButtonVisible,
            click: async () => {
              this.currentAction = TEMPLATE_ACTION_INDEX.APPROVEPURCHASE;
              this.$emit('open', this.item);
            },
          },
        },
        {
          id: ACTIONS.ConfirmPurchase,
          action: {
            name: 'Confirmar Compra',
            icon: 'fas fa-check-circle text-success',
            visible: this.isPurchaseConfirmButtonVisible,
            click: async () => {
              this.currentAction = TEMPLATE_ACTION_INDEX.CONFIRMPURCHASE;
              this.$emit('open', this.item);
            },
          },
        },
        {
          id: ACTIONS.PurchaseInfo,
          action: {
            name: 'Información de compra',
            icon: 'fas fa-usd-circle text-success',
            visible: this.item?.hasPurchases,
            click: async () => {
              this.showPurchaseInfo();
            },
          },
        },
        {
          id: ACTIONS.DeliverToCsee,
          action: {
            name: 'Recibido CSEE/ORE',
            icon: 'fas fa-check-circle text-success',
            visible: this.isDeliveryToCseeVisible,
            click: async () => {
              this.deliverToCsee();
            },
          },
        },
      ];

      return actions
        .filter((x) => this.actionsToShow.includes(x.id))
        .map((x) => x.action);
    },
    userName() {
      return this.item?.user?.name || '';
    },
    showTrackedInfo() {
      return this.currentAction == TEMPLATE_ACTION_INDEX.INFO;
    },
    showRejectForm() {
      return this.currentAction == TEMPLATE_ACTION_INDEX.REJECTREQUEST;
    },
    showSchoolDeliveryForm() {
      return this.currentAction == TEMPLATE_ACTION_INDEX.DELIVERTOSCHOOL;
    },
    showTeacherDeliveryForm() {
      return this.currentAction == TEMPLATE_ACTION_INDEX.RECIVEDBYMASTER;
    },
    showApprovalPurchaseForm() {
      return this.currentAction == TEMPLATE_ACTION_INDEX.APPROVEPURCHASE;
    },
    showDeliveryToCseeForm() {
      return this.currentAction == TEMPLATE_ACTION_INDEX.DELIVERYTOCSEE;
    },
    showAssignedMaterial() {
      return this.currentAction == TEMPLATE_ACTION_INDEX.CHECKASSIGNEDBOOK;
    },
    showConfirmationPurchaseForm() {
      return this.currentAction == TEMPLATE_ACTION_INDEX.CONFIRMPURCHASE;
    },
    isPanelOpen() {
      return this.fromRequest ? this.isOpen : this.item.isOpen;
    },
    canSeeRejectForm() {
      return (
        this.userHasPermissions(
          'academic.materialsandequipments.requests.reject'
        ) &&
        this.item?.status?.id == studentMaterialStatus.PedienteDeterminacionCSEE
      );
    },
    canViewInventory() {
      const invalidStatus = [
        studentMaterialStatus.PendienteCompra,
        studentMaterialStatus.NoAprobado,
        studentMaterialStatus.Entregado,
      ];
      return !invalidStatus.includes(this.item.status.id);
    },
    canSeeSchoolDeliveryForm() {
      return (
        this.item.status.id == studentMaterialStatus.PendienteEntregaEscuela
      );
    },
    canSeeMasterDeliveryForm() {
      return (
        this.item.status.id == studentMaterialStatus.PendienteEntregaMaestro
      );
    },
    isPurchaseApprovalButtonVisible() {
      return (
        this.item?.status?.id ==
          studentMaterialStatus.PedienteDeterminacionSAEE &&
        this.userHasPermissions(
          'academic.materialsandequipments.requests.approvepurchase'
        )
      );
    },
    isPurchaseConfirmButtonVisible() {
      return (
        this.item?.status?.id == studentMaterialStatus.PendienteCompra &&
        this.userHasPermissions(
          'academic.materialsandequipments.requests.confirmpurchase'
        )
      );
    },
    isDeliveryToCseeVisible() {
      return (
        this.item?.status?.id == studentMaterialStatus.PendienteEntregaCSEE &&
        this.userHasPermissions(
          'academic.materialsandequipments.requests.documentdelivery'
        )
      );
    },
    canSeeAssignedMaterial() {
      return Boolean(this.itemId);
    },
    isCenterToCenter() {
      return (
        Boolean(this.item?.transfer) &&
        this.item.reason.id == purchaseReasonsConst.IntercambioDeCentro
      );
    },
    transfer() {
      return this.item?.transfer || {};
    },
    showTransaction() {
      const { name } = this.$route;
      return [
        'materials-and-equipments-requests',
        'inventoryTranferMaterial',
        'materials-and-equipments-requests-manage',
      ].includes(name);
    },
    isValidTransaction() {
      return (
        this.showTransaction &&
        this.transactionMaterial?.title &&
        this.transactionOrder?.title
      );
    },
    deliveryToCseeMinDate() {
      const date = this.item.transfer
        ? this.item.transfer.sendDate
        : this.item.purchaseOrderConfirmationDate;

      return moment(date)
        .add(-1, 'days')
        .toDate();
    },
    transactionMaterial() {
      switch (this.item.assignReasonId) {
        case inventorystudentMaterialStatusReasons.InventarioCentro:
        case inventorystudentMaterialStatusReasons.IntercambioCentro:
          const center = this.item?.bookAssignedReasonCenter;
          return {
            title: center?.name ?? 'Oficina Central',
            color: center?.color ?? '#D3D3D3',
            icon: 'fas fa-map-marker-alt',
          };
        case inventorystudentMaterialStatusReasons.InventarioEscuela:
        case inventorystudentMaterialStatusReasons.IntercambioEscuela:
          const school = this.item?.bookAssignedReasonSchool;
          return {
            title: school?.name,
            color: school?.color ?? '#AAB7B1',
            icon: 'fas fa-school',
          };
        default:
          return {};
      }
    },
    transactionOrder() {
      switch (this.item.assignReasonId) {
        case inventorystudentMaterialStatusReasons.IntercambioCentro:
          const center = this.item?.center;
          const { centerTo, centerToColor } = this.item?.transfer
            ? this.item?.transfer
            : { centerTo: center?.name, centerToColor: center.color };
          return {
            title: centerTo,
            color: centerToColor ?? '#BF7AF0',
            icon: 'fas fa-map-marker-alt',
          };
        case inventorystudentMaterialStatusReasons.InventarioCentro:
        case inventorystudentMaterialStatusReasons.IntercambioEscuela:
          const school = this.item?.school;
          return {
            title: school?.name,
            color: school?.color ?? '#AAB7B1',
            icon: 'fas fa-school',
          };
        case inventorystudentMaterialStatusReasons.InventarioEscuela:
          return {
            title: 'Maestro',
            color: '#FF7058',
            icon: 'fas fa-user',
            bgColor: '#FFF5E8',
          };
        default:
          return {};
      }
    },
    itemId() {
      return this.item.itemId;
    },
    hasDocuments() {
      return Boolean(this.item.documents.length);
    },
  },
  methods: {
    async onToggle() {
      this.selectedTemplateTabIndex = null;

      if (this.item.isOpen) {
        this.isOpen = false;
        this.$emit('close', this.item);
        return;
      }

      this.currentAction = TEMPLATE_ACTION_INDEX.INFO;
      this.selectedTemplateTabIndex = TEMPLATE_TAB_INDEX.TRACKEDREQUEST;
      this.isOpen = true;
      this.$emit('open', this.item);
    },
    setCurrentTab(tab) {
      this.activeTab = tab;
    },
    onClosePanel() {
      this.currentAction = null;
      this.onToggle();
    },
    async rejectRequest(payload) {
      await inventoryOrderMaterialsAndEquipments.rejectRequest(payload);
      this.onToggle();
      this.$emit('refresh');
    },
    async deliveryCsee(payload) {
      await inventoryOrderMaterialsAndEquipments.deliveryCsee(payload);
      this.ShowToast(
        'Éxito',
        'La solicitud se ha guardado correctamente.',
        'success'
      );
      this.onToggle();
      this.$emit('refresh');
    },
    showRequestInfo() {
      this.currentAction = TEMPLATE_ACTION_INDEX.INFO;
      this.selectedTemplateTabIndex = TEMPLATE_TAB_INDEX.TRACKEDREQUEST;
      this.setCurrentTab(TEMPLATE_TAB_INDEX.TRACKEDREQUEST);
      this.$emit('open', this.item);
    },
    showPurchaseInfo() {
      this.currentAction = TEMPLATE_ACTION_INDEX.INFO;
      this.selectedTemplateTabIndex = TEMPLATE_TAB_INDEX.PURCHASEINFO;
      this.setCurrentTab(TEMPLATE_TAB_INDEX.PURCHASEINFO);
      this.$emit('open', this.item);
    },
    showOrderHistory() {
      this.currentAction = TEMPLATE_ACTION_INDEX.INFO;
      this.selectedTemplateTabIndex = TEMPLATE_TAB_INDEX.ORDERHISTORY;
      this.setCurrentTab(TEMPLATE_TAB_INDEX.ORDERHISTORY);
      this.$emit('open', this.item);
    },
    showDocuments() {
      this.currentAction = TEMPLATE_ACTION_INDEX.INFO;
      this.selectedTemplateTabIndex = TEMPLATE_TAB_INDEX.DOCUMENTS;
      this.setCurrentTab(TEMPLATE_TAB_INDEX.DOCUMENTS);
      this.$emit('open', this.item);
    },
    async changeStatusToPendingDeliveryTeacher(payload) {
      await inventoryOrderMaterialsAndEquipments.changeStatusToPendingDeliveryTeacher(
        payload
      );
      this.onToggle();
      this.$emit('refresh');
    },
    async changeStatusToDelivered(payload) {
      await inventoryOrderMaterialsAndEquipments.changeStatusToDelivered(
        payload
      );
      this.onToggle();
      this.$emit('refresh');
    },
    async approveOrderPurchase(payload) {
      await inventoryOrderMaterialsAndEquipments.approveOrderPurchase(payload);
      this.onToggle();
      this.$emit('refresh');
    },
    async deliverToCsee() {
      if (
        !this.item.transfer ||
        (this.item.transfer &&
          this.item.transfer.statusId === transferStatuses.OnTransferRoute)
      ) {
        this.currentAction = TEMPLATE_ACTION_INDEX.DELIVERYTOCSEE;
        this.$emit('open', this.item);
        return;
      }

      this.ShowToast(
        'Error',
        'El pedido cuenta con una solicitud de transferencia pendiente.',
        'error'
      );
    },
    async getMaterialDetail() {
      if (!this.itemId) return;
      const { data } = await inventoryMaterialsAndEquipments.getMaterial(
        this.itemId
      );
      this.material = data;
      this.material.isOpen = false;
    },
    async onConfirmPurchase(payload) {
      await inventoryOrderMaterialsAndEquipments.confirmPurchase(payload);
      this.onToggle();
      this.$emit('refresh');
    },
    handleAction() {
      this.material.isOpen = !this.material.isOpen;
    },
  },
};
</script>
<style scoped lang="scss">
::v-deep .balloon-container.success {
  background-color: #a3e5b3;
}
::v-deep .balloon-container.warning {
  background-color: #f8b539;
}

.border-radius-top-auto ::v-deep .border-radius--light {
  border-top-left-radius: 0 !important;
  border-top-right-radius: 0 !important;
}

.bg-custom-light-green {
  background-color: #d9e5dfba;
}
.bg-custom-light-tracked {
  background-color: #f7f7f7;
}
.custom-tab {
  --tab-active-color: #ff7058 !important;
  // padding-left: 12px !important;
}
</style>
